import React, { useState, useEffect } from 'react';
import MyScorecardModal from './MyScorecardModal';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001/api'
  : 'https://tto-player-api.fly.dev/api';

const ScoringStats = ({ token, groupId, onClose }) => {
  const [data, setData] = useState(null);
  // Rounds tab (Oct 6, 2026): a tap on a round opens the player's own scorecard.
  const [cardTeetimeId, setCardTeetimeId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [h2hMode, setH2hMode] = useState('net'); // 'net' or 'gross'
  const [lbMode, setLbMode] = useState('gross'); // 'gross' or 'net'
  const [lbExpanded, setLbExpanded] = useState(false);
  const [h2hExpanded, setH2hExpanded] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [courseStats, setCourseStats] = useState(null);
  const [courseLoading, setCourseLoading] = useState(false);
  // Round Insight (Oct 5, 2026): the ✨ on the latest round opens a private read on it.
  const [insightPeek, setInsightPeek] = useState(null);
  const [insightOpen, setInsightOpen] = useState(false);
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  const [insightError, setInsightError] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${API_URL}/player/scoring/stats?token=${token}`);
        if (resp.ok) setData(await resp.json());
      } catch (e) { console.error('Failed to load stats:', e); }
      finally { setLoading(false); }
    };
    load();
  }, [token]);

  // Which round gets the ✨ — a quick check that writes nothing.
  useEffect(() => {
    const load = async () => {
      try {
        const resp = await fetch(`${API_URL}/player/scoring/insight?peek=1&token=${token}`);
        if (resp.ok) setInsightPeek(await resp.json());
      } catch (e) { /* no ✨ — the table works as before */ }
    };
    load();
  }, [token]);

  const openInsight = async () => {
    setInsightOpen(true);
    if (insight && insightPeek && insight.teetime_id === insightPeek.teetime_id) return;
    setInsightLoading(true);
    setInsightError(false);
    try {
      const resp = await fetch(`${API_URL}/player/scoring/insight?token=${token}`);
      if (!resp.ok) throw new Error(String(resp.status));
      setInsight(await resp.json());
    } catch (e) {
      console.error('Failed to load round insight:', e);
      setInsightError(true);
    } finally { setInsightLoading(false); }
  };

  // Load course hole-by-hole stats when a course is selected
  useEffect(() => {
    if (!selectedCourse) { setCourseStats(null); return; }
    const load = async () => {
      setCourseLoading(true);
      try {
        const resp = await fetch(`${API_URL}/player/scoring/course-stats/${selectedCourse}?token=${token}`);
        if (resp.ok) setCourseStats(await resp.json());
      } catch (e) { console.error('Failed to load course stats:', e); }
      finally { setCourseLoading(false); }
    };
    load();
  }, [selectedCourse, token]);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
      <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
      <div style={{ fontSize: 16 }}>Loading your stats...</div>
    </div>
  );

  // Wall of Fame group IDs that should always show the tab with hardcoded data
  const SAMUELSON_GROUP_ID = '11111111-1111-1111-1111-111111111111';
  const DEMO_GROUP_ID = '0431809c-366b-48e3-ad8b-cecb9206a467';
  const wofHasContent = groupId === SAMUELSON_GROUP_ID || groupId === DEMO_GROUP_ID;

  // No scoring data yet: show empty state, but still offer Wall of Fame for Samuelson/Demo
  // (and a recruiting message for other groups)
  if (!data || !data.stats || data.stats.total_rounds === 0) return (
    <div style={{ maxWidth: 540, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 800 }}>📊 Scoring Stats</div>
        <button onClick={onClose} style={{ padding: '8px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>← Back</button>
      </div>
      <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', background: '#fff', borderRadius: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 48, marginBottom: 12 }}>⛳</div>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#111827' }}>No Scoring Data Yet</div>
        <div style={{ fontSize: 14 }}>Enter scores for at least one full round to see your stats.</div>
      </div>
      {/* Wall of Fame is still reachable below */}
      <WallOfFameSection groupId={groupId} token={token} />
    </div>
  );

  const { stats, distribution, rounds, group_leaderboard, head_to_head, courses_played } = data;

  const formatDate = (ds) => {
    if (!ds) return '';
    const d = new Date(ds.split('T')[0] + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const totalHolesScored = (distribution.eagle_or_better + distribution.birdie + distribution.par + distribution.bogey + distribution.double_bogey + distribution.triple_plus) || 1;

  const distPct = (val) => Math.round((val / totalHolesScored) * 100);

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'rounds', label: 'Rounds' },
    { id: 'courses', label: 'Courses' },
    { id: 'group', label: 'Group' },
    { id: 'wallOfFame', label: 'Wall of Fame' }
  ];

  // GIR helpers (Sept 29, 2026): a subtotal's GIR % from its holes' counts, and a
  // shade for the greens missed most often (under 20% red, under 30% amber).
  const girSub = (hs) => {
    const holes = hs.reduce((s, x) => s + (x.gir_holes || 0), 0);
    const hit = hs.reduce((s, x) => s + (x.gir_hit || 0), 0);
    return holes > 0 ? `${Math.round((hit / holes) * 100)}%` : '—';
  };
  const girBg = (pct) => (pct == null ? 'transparent' : pct < 20 ? '#fee2e2' : pct < 30 ? '#fef3c7' : 'transparent');

  // Helper: color for avg vs par values
  const vsParColor = (vsPar) => {
    if (vsPar <= -0.1) return '#16a34a';
    if (vsPar <= 0.3) return '#22c55e';
    if (vsPar <= 0.6) return '#f59e0b';
    if (vsPar <= 1.0) return '#ea580c';
    return '#dc2626';
  };

  return (
    <div style={{ maxWidth: 540, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 22, fontWeight: 800 }}>📊 Scoring Stats</div>
        <button onClick={onClose} style={{ padding: '8px 14px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>← Back</button>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, background: '#f3f4f6', borderRadius: 10, padding: 4, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            style={{
              flex: '1 1 auto', minWidth: 'fit-content', padding: '8px 8px', border: 'none', borderRadius: 8, cursor: 'pointer',
              fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap',
              background: activeTab === t.id ? '#fff' : 'transparent',
              color: activeTab === t.id ? '#166534' : '#6b7280',
              boxShadow: activeTab === t.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* ===== OVERVIEW TAB ===== */}
      {activeTab === 'overview' && (
        <div>
          {/* Hero stat */}
          <div style={{ background: 'linear-gradient(135deg, #1f2937, #111827)', borderRadius: 14, padding: 24, color: '#fff', textAlign: 'center', marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', inset: 0, opacity: 0.03, backgroundImage: 'radial-gradient(circle at 2px 2px, #fff 1px, transparent 0)', backgroundSize: '24px 24px' }} />
            <div style={{ position: 'relative' }}>
              <div style={{ fontSize: 12, opacity: 0.6, fontWeight: 700, letterSpacing: 2, marginBottom: 6 }}>SCORING AVERAGE</div>
              <div style={{ fontSize: 56, fontWeight: 900, lineHeight: 1 }}>{stats.scoring_avg ? Number(stats.scoring_avg).toFixed(1) : '—'}</div>
              {stats.last_5_avg && stats.scoring_avg && (() => {
                const diff5 = Number(stats.last_5_avg) - Number(stats.scoring_avg);
                const trending = diff5 > 0.3 ? 'up' : diff5 < -0.3 ? 'down' : 'flat';
                const trendColor = trending === 'down' ? 'rgba(74,222,128,0.2)' : trending === 'up' ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.1)';
                const arrow = trending === 'down' ? '▼' : trending === 'up' ? '▲' : '—';
                const label = trending === 'flat' ? 'Steady' : `${Math.abs(diff5).toFixed(1)} last 5`;
                return (
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: trendColor, borderRadius: 20, padding: '3px 10px', marginTop: 8 }}>
                    <span style={{ fontSize: 11 }}>{arrow}</span>
                    <span style={{ fontSize: 12, fontWeight: 600 }}>{label}</span>
                  </div>
                );
              })()}
              <div style={{ display: 'flex', justifyContent: 'center', gap: 24, marginTop: 10, fontSize: 13, opacity: 0.7 }}>
                <span>Last 5: <strong style={{ opacity: 1 }}>{stats.last_5_avg ? Number(stats.last_5_avg).toFixed(1) : '—'}</strong></span>
                <span>Last 10: <strong style={{ opacity: 1 }}>{stats.last_10_avg ? Number(stats.last_10_avg).toFixed(1) : '—'}</strong></span>
                <span>Rounds: <strong style={{ opacity: 1 }}>{stats.total_rounds}</strong></span>
              </div>
            </div>
          </div>

          {/* Key numbers grid - color-coded */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            {[
              { label: 'Best Round', value: stats.best_round?.gross || '—', sub: stats.best_round ? `${stats.best_round.course} • ${formatDate(stats.best_round.date)}` : '', accent: '#16a34a', bg: '#f0fdf4', icon: '🏆' },
              { label: 'Worst (Last 20)', value: stats.worst_round?.gross || '—', sub: stats.worst_round ? `${stats.worst_round.course} • ${formatDate(stats.worst_round.date)}` : '', accent: '#dc2626', bg: '#fef2f2', icon: '📉' },
              { label: 'Putts / Round', value: stats.putts_avg ? Number(stats.putts_avg).toFixed(1) : '—', sub: 'Average when tracked', accent: '#6b7280', bg: '#f9fafb', icon: '🎯' },
              { label: '3-Putts / Round', value: stats.three_putt_avg != null ? Number(stats.three_putt_avg).toFixed(1) : '—', sub: stats.three_putt_avg !== null ? (stats.three_putt_avg <= 2 ? '✓ Good' : stats.three_putt_avg <= 3 ? 'Average' : 'Needs work') : '', accent: stats.three_putt_avg !== null && stats.three_putt_avg <= 2 ? '#16a34a' : '#f59e0b', bg: stats.three_putt_avg !== null && stats.three_putt_avg <= 2 ? '#f0fdf4' : '#fffbeb', icon: stats.three_putt_avg !== null && stats.three_putt_avg <= 2 ? '✅' : '⚠️' },
              { label: 'GIR / Round', value: stats.gir_per_round != null ? Number(stats.gir_per_round).toFixed(1) : '—', sub: stats.gir_pct != null ? `${Number(stats.gir_pct).toFixed(0)}% of holes · est. from putts` : 'Greens in regulation', accent: '#2563eb', bg: '#eff6ff', icon: '⛳' },
              { label: 'Handicap', value: stats.handicap !== null ? Number(stats.handicap).toFixed(1) : '—', sub: 'Current index', accent: '#7c3aed', bg: '#f5f3ff', icon: '📊' },
            ].map((item, i) => (
              <div key={i} style={{
                background: item.bg,
                borderRadius: 12,
                padding: '14px 14px 12px',
                border: `1px solid ${item.accent}22`,
                borderLeft: `4px solid ${item.accent}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{item.label}</div>
                  <div style={{ fontSize: 14 }}>{item.icon}</div>
                </div>
                <div style={{ fontSize: 28, fontWeight: 800, color: '#1f2937' }}>{item.value}</div>
                {item.sub && <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{item.sub}</div>}
              </div>
            ))}
          </div>

          {/* Par breakdown with gauge bars */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: '#374151' }}>Scoring by Par</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              {[
                { par: 3, avg: stats.par_3_avg, gir: stats.gir_par_3_pct },
                { par: 4, avg: stats.par_4_avg, gir: stats.gir_par_4_pct },
                { par: 5, avg: stats.par_5_avg, gir: stats.gir_par_5_pct }
              ].map(p => {
                const diff = p.avg ? (p.avg - p.par) : null;
                const color = diff === null ? '#9ca3af' : diff <= 0 ? '#16a34a' : diff <= 0.5 ? '#22c55e' : diff <= 1 ? '#f59e0b' : '#dc2626';
                const gaugeWidth = diff !== null ? Math.min(Math.max((diff / 2) * 100, 0), 100) : 0;
                return (
                  <div key={p.par} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, marginBottom: 6 }}>Par {p.par}s</div>
                    <div style={{ fontSize: 30, fontWeight: 900, color, lineHeight: 1 }}>{p.avg ? Number(p.avg).toFixed(2) : '—'}</div>
                    <div style={{ margin: '8px auto 0', width: '80%', height: 6, background: '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${gaugeWidth}%`, background: color, borderRadius: 3 }} />
                    </div>
                    {diff !== null && <div style={{ fontSize: 12, color, fontWeight: 700, marginTop: 4 }}>{diff > 0 ? '+' : ''}{diff.toFixed(2)}</div>}
                    {p.gir != null && <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600, marginTop: 6 }}>GIR {Math.round(p.gir)}%</div>}
                  </div>
                );
              })}
            </div>
            {[stats.gir_par_3_pct, stats.gir_par_4_pct, stats.gir_par_5_pct].some(v => v != null) && (
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, textAlign: 'center' }}>GIR = greens in regulation, estimated from putts</div>
            )}
          </div>

          {/* Score distribution - improved */}
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 14, color: '#374151' }}>Score Distribution</div>
            {[
              { label: 'Eagle+', count: distribution.eagle_or_better, color: '#fbbf24' },
              { label: 'Birdie', count: distribution.birdie, color: '#dc2626' },
              { label: 'Par', count: distribution.par, color: '#16a34a' },
              { label: 'Bogey', count: distribution.bogey, color: '#2563eb' },
              { label: 'Double', count: distribution.double_bogey, color: '#7c3aed' },
              { label: 'Triple+', count: distribution.triple_plus, color: '#374151' }
            ].map(d => {
              const pct = distPct(d.count);
              const barW = Math.max(pct, d.count > 0 ? 4 : 0);
              return (
                <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 6 }}>
                  <div style={{ width: 60, fontSize: 12, fontWeight: 600, color: '#374151', textAlign: 'right', paddingRight: 10 }}>{d.label}</div>
                  <div style={{ flex: 1, height: 28, background: '#f3f4f6', borderRadius: 6, overflow: 'hidden', position: 'relative' }}>
                    <div style={{
                      height: '100%', width: `${barW}%`, background: d.color, borderRadius: 6,
                      display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                      paddingRight: barW > 15 ? 8 : 0,
                    }}>
                      {barW > 15 && <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>{d.count}</span>}
                    </div>
                    {barW <= 15 && d.count > 0 && (
                      <span style={{ position: 'absolute', left: `calc(${barW}% + 6px)`, top: '50%', transform: 'translateY(-50%)', fontSize: 11, fontWeight: 700, color: '#6b7280' }}>{d.count}</span>
                    )}
                  </div>
                  <div style={{ width: 40, fontSize: 12, fontWeight: 600, color: '#9ca3af', textAlign: 'right' }}>{pct}%</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== ROUNDS TAB ===== */}
      {activeTab === 'rounds' && (
        <div>
          {/* Scoring trend - simple bar chart */}
          {rounds.length > 1 && (() => {
            const chartRounds = [...rounds].reverse().slice(-20);
            const minScore = Math.min(...chartRounds.map(x => x.gross_total));
            const maxScore = Math.max(...chartRounds.map(x => x.gross_total));
            const range = maxScore - minScore || 1;
            return (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, color: '#374151' }}>Scoring Trend</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 140, paddingTop: 20 }}>
                {chartRounds.map((r, i) => {
                  const barHeight = Math.round(24 + ((r.gross_total - minScore) / range) * 96);
                  const isLatest = i === chartRounds.length - 1;
                  return (
                    <div key={r.teetime_id} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}>
                      <div style={{ fontSize: 9, color: isLatest ? '#166534' : '#9ca3af', fontWeight: isLatest ? 700 : 400, marginBottom: 2 }}>
                        {r.gross_total}
                      </div>
                      <div style={{
                        width: '100%', maxWidth: 20, height: barHeight,
                        background: isLatest ? '#1b4332' : r.gross_total <= (stats.scoring_avg || 999) ? '#4ade80' : '#d1d5db',
                        borderRadius: 3, minHeight: 4
                      }}></div>
                    </div>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 10, color: '#9ca3af' }}>
                <span>{formatDate(chartRounds[0]?.date)}</span>
                <span>Last {chartRounds.length} rounds</span>
                <span>{formatDate(chartRounds[chartRounds.length - 1]?.date)}</span>
              </div>
            </div>
            );
          })()}

          {/* Rounds table (Sept 29, 2026 — Dennis): one row per round — date, course,
              score, putts, 3-putts and GIR — in place of the round cards and the Putting
              Trend chart. A dash = putts not entered hole by hole, so unknown. GIR is
              estimated from putts (score minus putts, par - 2 or better). */}
          {/* Round Insight (Oct 5, 2026): only the latest round gets one. */}
          {insightPeek && insightPeek.eligible && (
            <button onClick={openInsight}
              style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '14px 16px', marginBottom: 12, background: '#fffbeb', border: '2px solid #fcd34d', borderRadius: 12, cursor: 'pointer', textAlign: 'left' }}>
              <span style={{ fontSize: 26, flexShrink: 0 }}>✨</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 17, fontWeight: 800, color: '#1f2937' }}>Insights on your latest round</span>
                <span style={{ display: 'block', fontSize: 14, color: '#6b7280' }}>{insightPeek.course_name} · {formatDate(insightPeek.date)} — tap here</span>
              </span>
              <span style={{ fontSize: 22, color: '#b45309', flexShrink: 0 }}>›</span>
            </button>
          )}
          {insightPeek && !insightPeek.eligible && insightPeek.reason !== 'no_rounds' && insightPeek.needed != null && (
            <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12, padding: '0 4px' }}>
              ✨ Insights start after {insightPeek.needed} rounds at a course
              {insightPeek.course_name ? ` — ${Math.max(0, insightPeek.needed - (insightPeek.prior_rounds || 0))} more at ${insightPeek.course_name}` : ''}.
            </div>
          )}

          {(() => {
            const dash = (v) => (v == null ? '—' : v);
            const col = { score: 48, putts: 48, three: 30, gir: 38 };
            const cell = (w, extra = {}) => ({ width: w, flexShrink: 0, textAlign: 'center', ...extra });
            const fmt1 = (v) => (v == null ? '—' : Number(v).toFixed(1));
            return (
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', background: '#1f2937', color: '#fff', fontSize: 11, fontWeight: 700, letterSpacing: 0 }}>
                  <div style={{ width: 52, flexShrink: 0 }}>DATE</div>
                  <div style={{ flex: 1, minWidth: 0 }}>COURSE</div>
                  <div style={cell(col.score)}>SCORE</div>
                  <div style={cell(col.putts)}>PUTTS</div>
                  <div style={cell(col.three)}>3P</div>
                  <div style={cell(col.gir)}>GIR</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', background: '#f0fdf4', borderBottom: '1px solid #e5e7eb', fontSize: 12, fontWeight: 700, color: '#166534' }}>
                  <div style={{ width: 52, flexShrink: 0 }}>Avg</div>
                  <div style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{stats.total_rounds} round{stats.total_rounds !== 1 ? 's' : ''}</div>
                  <div style={cell(col.score, { fontSize: 15, fontWeight: 800 })}>{fmt1(stats.scoring_avg)}</div>
                  <div style={cell(col.putts, { fontSize: 15, fontWeight: 800 })}>{fmt1(stats.putts_avg)}</div>
                  <div style={cell(col.three)}>{fmt1(stats.three_putt_avg)}</div>
                  <div style={cell(col.gir, { fontSize: 15, fontWeight: 800 })}>{fmt1(stats.gir_per_round)}</div>
                </div>
                <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                  {rounds.map((r, i) => {
                    // Scorecard on a tap (Oct 6, 2026): organized rounds entered hole by
                    // hole. A total-only entry has no holes to show; a personal round's
                    // card lives in My Rounds. The Insight stays in the box above.
                    const hasCard = r.entry_type === 'hole_by_hole' && (r.round_source || 'organized') === 'organized';
                    return (
                    <div key={r.teetime_id}
                      onClick={hasCard ? () => setCardTeetimeId(r.teetime_id) : undefined}
                      role={hasCard ? 'button' : undefined}
                      data-testid={hasCard ? 'stats-round-card' : undefined}
                      style={{ display: 'flex', alignItems: 'center', padding: '11px 10px', borderBottom: '1px solid #f3f4f6', background: i % 2 ? '#fafafa' : '#fff', fontSize: 13, cursor: hasCard ? 'pointer' : 'default' }}>
                      <div style={{ width: 52, flexShrink: 0, fontSize: 12, color: '#6b7280', fontWeight: 600 }}>{formatDate(r.date)}</div>
                      <div style={{ flex: 1, minWidth: 0, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: hasCard ? '#166534' : '#1f2937' }}>
                        {r.course_name}
                        {r.holes_scored < 18 && <span style={{ fontSize: 11, color: '#9ca3af', fontWeight: 500 }}> · {r.holes_scored} holes</span>}
                      </div>
                      <div style={cell(col.score, { fontSize: 17, fontWeight: 800, color: '#1f2937' })}>{r.gross_total}</div>
                      <div style={cell(col.putts, { fontWeight: 700, color: r.putts_total == null ? '#9ca3af' : '#374151' })}>{dash(r.putts_total)}</div>
                      <div style={cell(col.three, { color: r.three_putts == null ? '#9ca3af' : '#6b7280' })}>{dash(r.three_putts)}</div>
                      <div style={cell(col.gir, { fontWeight: 700, color: r.gir == null ? '#9ca3af' : '#374151' })}>{dash(r.gir)}</div>
                    </div>
                    );
                  })}
                </div>
                <div style={{ padding: '8px 10px', fontSize: 11, color: '#9ca3af', borderTop: '1px solid #f3f4f6' }}>
                  Newest first. Tap a round in green to see its scorecard. — = putts not entered hole by hole. GIR is estimated from putts.
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ===== COURSES TAB ===== */}
      {activeTab === 'courses' && (
        <div>
          {(!courses_played || courses_played.length === 0) ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>⛳</div>
              <div style={{ fontSize: 14 }}>Course stats appear when you have scored rounds.</div>
            </div>
          ) : !selectedCourse ? (
            <div style={{ display: 'grid', gap: 8 }}>
              {courses_played.map(c => (
                <button key={c.course_id} onClick={() => setSelectedCourse(c.course_id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, cursor: 'pointer', textAlign: 'left', width: '100%' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#166534'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#e5e7eb'}>
                  <div style={{ width: 44, height: 44, borderRadius: 10, background: 'linear-gradient(135deg, #166534, #1b4332)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ fontSize: 20 }}>⛳</span>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.course_name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{c.rounds_played} round{c.rounds_played !== 1 ? 's' : ''} • Best: {c.best_score}</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#1f2937' }}>{c.avg_score ? c.avg_score.toFixed(1) : '—'}</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>avg</div>
                  </div>
                  <div style={{ fontSize: 16, color: '#9ca3af', flexShrink: 0 }}>›</div>
                </button>
              ))}
            </div>
          ) : (
            <div>
              <button onClick={() => { setSelectedCourse(null); setCourseStats(null); }}
                style={{ padding: '6px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 12 }}>← All Courses</button>
              {courseLoading ? (
                <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 16 }}>Loading course stats...</div>
              ) : courseStats && courseStats.hole_stats.length > 0 ? (
                <div>
                  {/* Course header */}
                  <div style={{ background: 'linear-gradient(135deg, #1f2937, #111827)', borderRadius: 12, padding: 18, color: '#fff', marginBottom: 14, textAlign: 'center' }}>
                    <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 4 }}>{courseStats.course_name}</div>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 20, fontSize: 13, opacity: 0.8 }}>
                      <span>{courseStats.rounds_played} round{courseStats.rounds_played !== 1 ? 's' : ''}</span>
                      <span>Avg: <strong style={{ opacity: 1, fontSize: 16 }}>{courseStats.summary?.avg_total || '—'}</strong></span>
                      {courseStats.summary?.avg_vs_par !== null && <span>({courseStats.summary.avg_vs_par > 0 ? '+' : ''}{courseStats.summary.avg_vs_par})</span>}
                    </div>
                  </div>
                  {/* Trouble / Strength callouts */}
                  {courseStats.summary?.worst_hole && courseStats.summary?.best_hole && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 12px', borderLeft: '4px solid #dc2626' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#dc2626', marginBottom: 2 }}>TOUGHEST HOLE</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#1f2937' }}>#{courseStats.summary.worst_hole.hole}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>Par {courseStats.summary.worst_hole.par} • +{courseStats.summary.worst_hole.avg_vs_par.toFixed(2)}/round</div>
                      </div>
                      <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 12px', borderLeft: '4px solid #16a34a' }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginBottom: 2 }}>STRONGEST HOLE</div>
                        <div style={{ fontSize: 20, fontWeight: 800, color: '#1f2937' }}>#{courseStats.summary.best_hole.hole}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>Par {courseStats.summary.best_hole.par} • {courseStats.summary.best_hole.avg_vs_par > 0 ? '+' : ''}{courseStats.summary.best_hole.avg_vs_par.toFixed(2)}/round</div>
                      </div>
                    </div>
                  )}
                  {/* Greens missed most (Sept 29, 2026): the three lowest GIR % holes with
                      putts entered at least 3 times, and GIR on the course overall. */}
                  {courseStats.summary?.gir_missed_most?.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 12px', borderLeft: '4px solid #2563eb', marginBottom: 14 }}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', marginBottom: 2 }}>GREENS YOU MISS MOST</div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: '#1f2937' }}>
                          {(() => { const hs = courseStats.summary.gir_missed_most.map(h => `#${h.hole}`); return hs.length > 1 ? `${hs.slice(0, -1).join(', ')} and ${hs[hs.length - 1]}` : hs[0]; })()}
                        </div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>Hit {courseStats.summary.gir_missed_most.map(h => `${h.gir_pct}%`).join(', ')} of the time</div>
                      </div>
                      {courseStats.summary.gir_pct != null && (
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8' }}>GIR HERE</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: '#1f2937' }}>{courseStats.summary.gir_pct}%</div>
                        </div>
                      )}
                    </div>
                  )}
                  {/* Par breakdown for this course */}
                  {courseStats.summary && (
                    <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                      {[
                        { par: 3, avg: courseStats.summary.par3_avg, vs: courseStats.summary.par3_vs_par },
                        { par: 4, avg: courseStats.summary.par4_avg, vs: courseStats.summary.par4_vs_par },
                        { par: 5, avg: courseStats.summary.par5_avg, vs: courseStats.summary.par5_vs_par }
                      ].filter(p => p.avg !== null).map(p => (
                        <div key={p.par} style={{ flex: 1, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 700 }}>Par {p.par}s</div>
                          <div style={{ fontSize: 22, fontWeight: 800, color: vsParColor(p.vs) }}>{p.avg.toFixed(2)}</div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: vsParColor(p.vs) }}>{p.vs > 0 ? '+' : ''}{p.vs.toFixed(2)}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Hole-by-hole table */}
                  <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', background: '#1f2937', color: '#fff', padding: '8px 0', fontSize: 11, fontWeight: 700, letterSpacing: 0.5 }}>
                      <div style={{ width: 38, textAlign: 'center' }}>HOLE</div>
                      <div style={{ width: 42, textAlign: 'center' }}>YDS</div>
                      <div style={{ width: 34, textAlign: 'center' }}>PAR</div>
                      <div style={{ width: 34, textAlign: 'center' }}>HCP</div>
                      <div style={{ flex: 1, textAlign: 'center' }}>AVG</div>
                      <div style={{ width: 50, textAlign: 'center' }}>+/−</div>
                      <div style={{ width: 44, marginRight: 4, textAlign: 'center' }}>GIR</div>
                    </div>
                    {courseStats.hole_stats.map((h, idx) => {
                      const isWorst = h.hole === courseStats.summary?.worst_hole?.hole;
                      const rows = [];
                      rows.push(
                        <div key={h.hole} style={{ display: 'flex', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f3f4f6', background: isWorst ? '#fef2f2' : h.hole % 2 === 0 ? '#fafafa' : '#fff' }}>
                          <div style={{ width: 38, textAlign: 'center', fontWeight: 800, fontSize: 14, color: '#374151' }}>{h.hole}</div>
                          <div style={{ width: 42, textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>{h.yardage || ''}</div>
                          <div style={{ width: 34, textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#6b7280' }}>{h.par}</div>
                          <div style={{ width: 34, textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>{h.hcp || ''}</div>
                          <div style={{ flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 800, color: '#1f2937' }}>{h.avg_score.toFixed(1)}</div>
                          <div style={{ width: 50, textAlign: 'center', fontSize: 13, fontWeight: 700, color: vsParColor(h.avg_vs_par) }}>{h.avg_vs_par > 0 ? '+' : ''}{h.avg_vs_par.toFixed(2)}</div>
                          <div style={{ width: 44, marginRight: 4, textAlign: 'center', fontSize: 12, fontWeight: 700, color: h.gir_pct == null ? '#d1d5db' : '#374151', background: girBg(h.gir_pct) }}>{h.gir_pct == null ? '—' : `${h.gir_pct}%`}</div>
                        </div>
                      );
                      // OUT subtotal after hole 9
                      if (h.hole === 9) {
                        const front = courseStats.hole_stats.filter(x => x.hole <= 9);
                        const fPar = front.reduce((s, x) => s + x.par, 0);
                        const fAvg = front.reduce((s, x) => s + x.avg_score, 0);
                        rows.push(
                          <div key="out" style={{ display: 'flex', alignItems: 'center', padding: '6px 0', background: '#f0f0f0', borderBottom: '2px solid #d1d5db', fontWeight: 700, fontSize: 12 }}>
                            <div style={{ width: 38, textAlign: 'center', color: '#374151' }}>OUT</div><div style={{ width: 42 }}></div>
                            <div style={{ width: 34, textAlign: 'center', color: '#6b7280' }}>{fPar}</div><div style={{ width: 34 }}></div>
                            <div style={{ flex: 1, textAlign: 'center', fontSize: 15, fontWeight: 800, color: '#166534' }}>{fAvg.toFixed(1)}</div>
                            <div style={{ width: 50, textAlign: 'center', color: vsParColor((fAvg - fPar) / 9) }}>{(fAvg - fPar) > 0 ? '+' : ''}{(fAvg - fPar).toFixed(1)}</div>
                            <div style={{ width: 44, marginRight: 4, textAlign: 'center', color: '#374151' }}>{girSub(front)}</div>
                          </div>
                        );
                      }
                      // IN subtotal + TOTAL after last hole
                      if (idx === courseStats.hole_stats.length - 1 && h.hole > 9) {
                        const back = courseStats.hole_stats.filter(x => x.hole > 9);
                        const bPar = back.reduce((s, x) => s + x.par, 0);
                        const bAvg = back.reduce((s, x) => s + x.avg_score, 0);
                        rows.push(
                          <div key="in" style={{ display: 'flex', alignItems: 'center', padding: '6px 0', background: '#f0f0f0', borderBottom: '2px solid #d1d5db', fontWeight: 700, fontSize: 12 }}>
                            <div style={{ width: 38, textAlign: 'center', color: '#374151' }}>IN</div><div style={{ width: 42 }}></div>
                            <div style={{ width: 34, textAlign: 'center', color: '#6b7280' }}>{bPar}</div><div style={{ width: 34 }}></div>
                            <div style={{ flex: 1, textAlign: 'center', fontSize: 15, fontWeight: 800, color: '#166534' }}>{bAvg.toFixed(1)}</div>
                            <div style={{ width: 50, textAlign: 'center', color: vsParColor((bAvg - bPar) / 9) }}>{(bAvg - bPar) > 0 ? '+' : ''}{(bAvg - bPar).toFixed(1)}</div>
                            <div style={{ width: 44, marginRight: 4, textAlign: 'center', color: '#374151' }}>{girSub(back)}</div>
                          </div>
                        );
                        const all = courseStats.hole_stats;
                        const tPar = all.reduce((s, x) => s + x.par, 0);
                        const tAvg = all.reduce((s, x) => s + x.avg_score, 0);
                        rows.push(
                          <div key="total" style={{ display: 'flex', alignItems: 'center', padding: '8px 0', background: '#1f2937', fontWeight: 800, fontSize: 13 }}>
                            <div style={{ width: 38, textAlign: 'center', color: '#fff' }}>TOT</div><div style={{ width: 42 }}></div>
                            <div style={{ width: 34, textAlign: 'center', color: '#9ca3af' }}>{tPar}</div><div style={{ width: 34 }}></div>
                            <div style={{ flex: 1, textAlign: 'center', fontSize: 18, fontWeight: 900, color: '#4ade80' }}>{tAvg.toFixed(1)}</div>
                            <div style={{ width: 50, textAlign: 'center', fontSize: 13, fontWeight: 700, color: '#4ade80' }}>{(tAvg - tPar) > 0 ? '+' : ''}{(tAvg - tPar).toFixed(1)}</div>
                            <div style={{ width: 44, marginRight: 4, textAlign: 'center', fontSize: 12, fontWeight: 700, color: '#bfdbfe' }}>{girSub(all)}</div>
                          </div>
                        );
                      }
                      return rows;
                    })}
                  </div>
                  {courseStats.hole_stats.some(h => h.gir_pct != null) && (
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>GIR = greens in regulation, estimated from putts. — = no putts entered on that hole.</div>
                  )}
                </div>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: '#6b7280', fontSize: 14 }}>No hole-by-hole data for this course yet.</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ===== GROUP TAB ===== */}
      {activeTab === 'group' && (
        <div>
          {/* Leaderboard with Gross/Net toggle */}
          {group_leaderboard.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '16px 16px 8px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>Group Leaderboard</div>
                <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 6, padding: 2 }}>
                  {['gross', 'net'].map(m => (
                    <button key={m} onClick={() => setLbMode(m)}
                      style={{ padding: '4px 10px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600, textTransform: 'capitalize', background: lbMode === m ? '#fff' : 'transparent', color: lbMode === m ? '#166534' : '#6b7280', boxShadow: lbMode === m ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}
                    >{m}</button>
                  ))}
                </div>
              </div>
              {(() => {
                const sorted = [...group_leaderboard].sort((a, b) => {
                  const aVal = lbMode === 'net' ? (a.net_scoring_avg || 999) : a.scoring_avg;
                  const bVal = lbMode === 'net' ? (b.net_scoring_avg || 999) : b.scoring_avg;
                  return aVal - bVal;
                });
                const LB_MAX = 5;
                const displayList = lbExpanded ? sorted : sorted.slice(0, LB_MAX);
                const meInList = displayList.some(p => p.is_me);
                const mePlayer = sorted.find(p => p.is_me);
                const meRank = sorted.findIndex(p => p.is_me);
                const showMePinned = !lbExpanded && !meInList && mePlayer && meRank >= LB_MAX;
                return (
                  <div>
                    {displayList.map((p, i) => {
                      const avgVal = lbMode === 'net' ? p.net_scoring_avg : p.scoring_avg;
                      const bestVal = lbMode === 'net' ? (p.best_net_round || p.best_round) : p.best_round;
                      return (
                        <div key={p.id} style={{ display: 'flex', alignItems: 'center', padding: '10px 8px', marginBottom: 4, borderBottom: '1px solid #f3f4f6', background: p.is_me ? '#f0fdf4' : 'transparent', borderRadius: 8, borderLeft: p.is_me ? '3px solid #166534' : '3px solid transparent' }}>
                          <div style={{ width: 24, flexShrink: 0, fontSize: 16, fontWeight: 800, color: i === 0 ? '#fbbf24' : i === 1 ? '#9ca3af' : i === 2 ? '#cd7f32' : '#d1d5db', textAlign: 'center' }}>{i + 1}</div>
                          <div style={{ flex: 1, minWidth: 0, marginLeft: 10 }}>
                            <div style={{ fontSize: 14, fontWeight: p.is_me ? 700 : 500, color: p.is_me ? '#166534' : '#374151' }}>{p.name} {p.is_me ? '(You)' : ''}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>HCP {p.handicap || 'N/A'} • {p.rounds_played} rds • Best: {bestVal}</div>
                          </div>
                          <div style={{ width: 70, flexShrink: 0, fontSize: 22, fontWeight: 800, color: p.is_me ? '#166534' : '#1f2937', textAlign: 'right' }}>{avgVal !== null ? Number(avgVal).toFixed(1) : '—'}</div>
                        </div>
                      );
                    })}
                    {showMePinned && (
                      <>
                        <div style={{ textAlign: 'center', padding: '4px 0', fontSize: 11, color: '#9ca3af' }}>• • •</div>
                        <div style={{ display: 'flex', alignItems: 'center', padding: '10px 8px', marginBottom: 4, background: '#f0fdf4', borderRadius: 8, borderLeft: '3px solid #166534' }}>
                          <div style={{ width: 24, flexShrink: 0, fontSize: 16, fontWeight: 800, color: '#d1d5db', textAlign: 'center' }}>{meRank + 1}</div>
                          <div style={{ flex: 1, minWidth: 0, marginLeft: 10 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>{mePlayer.name} (You)</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>HCP {mePlayer.handicap || 'N/A'} • {mePlayer.rounds_played} rds • Best: {lbMode === 'net' ? (mePlayer.best_net_round || mePlayer.best_round) : mePlayer.best_round}</div>
                          </div>
                          <div style={{ width: 70, flexShrink: 0, fontSize: 22, fontWeight: 800, color: '#166534', textAlign: 'right' }}>{(lbMode === 'net' ? mePlayer.net_scoring_avg : mePlayer.scoring_avg)?.toFixed(1) || '—'}</div>
                        </div>
                      </>
                    )}
                    {sorted.length > LB_MAX && (
                      <button onClick={() => setLbExpanded(!lbExpanded)} style={{ width: '100%', padding: '8px 0', marginTop: 4, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#166534' }}>
                        {lbExpanded ? 'Show Less' : `Show All (${sorted.length})`}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {/* Head to Head with collapse */}
          {head_to_head.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>Head to Head</div>
                <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 6, padding: 2 }}>
                  {['net', 'gross'].map(m => (
                    <button key={m} onClick={() => setH2hMode(m)}
                      style={{ padding: '4px 10px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600, textTransform: 'capitalize', background: h2hMode === m ? '#fff' : 'transparent', color: h2hMode === m ? '#166534' : '#6b7280', boxShadow: h2hMode === m ? '0 1px 2px rgba(0,0,0,0.1)' : 'none' }}
                    >{m}</button>
                  ))}
                </div>
              </div>
              {(() => {
                const H2H_MAX = 4;
                const displayH2H = h2hExpanded ? head_to_head : head_to_head.slice(0, H2H_MAX);
                return (
                  <div>
                    {displayH2H.map(h => {
                      const record = h2hMode === 'net' ? h.net : h.gross;
                      const wins = record.wins;
                      const losses = record.losses;
                      const total = wins + losses + (record.ties || 0);
                      const winPct = total > 0 ? Math.round((wins / total) * 100) : 0;
                      return (
                        <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f3f4f6' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 600 }}>{h.name}</div>
                            <div style={{ fontSize: 12, color: '#6b7280' }}>HCP {h.handicap || 'N/A'} • {h.rounds_together} rounds together</div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 16, fontWeight: 800, color: wins > losses ? '#16a34a' : wins < losses ? '#dc2626' : '#6b7280' }}>{wins}W-{losses}L{record.ties ? `-${record.ties}T` : ''}</div>
                            <div style={{ fontSize: 11, color: '#9ca3af' }}>{winPct}% win rate</div>
                          </div>
                        </div>
                      );
                    })}
                    {head_to_head.length > H2H_MAX && (
                      <button onClick={() => setH2hExpanded(!h2hExpanded)} style={{ width: '100%', padding: '8px 0', marginTop: 4, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#166534' }}>
                        {h2hExpanded ? 'Show Less' : `Show All (${head_to_head.length})`}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {group_leaderboard.length === 0 && head_to_head.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
              <div style={{ fontSize: 14 }}>Group stats appear when multiple players enter scores.</div>
            </div>
          )}
        </div>
      )}

      {/* ===== WALL OF FAME TAB ===== */}
      {/* Round Insight modal (Oct 5, 2026) — large type for older eyes; one Close. */}
      {cardTeetimeId && (
        <MyScorecardModal apiUrl={API_URL} token={token} teetimeId={cardTeetimeId} onClose={() => setCardTeetimeId(null)} />
      )}

      {insightOpen && (
        <div onClick={() => setInsightOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', padding: 22, boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#b45309', marginBottom: 6 }}>✨ YOUR LATEST ROUND</div>
            {insightLoading ? (
              <div style={{ padding: '30px 0', textAlign: 'center', fontSize: 17, color: '#6b7280' }}>Looking over your round…</div>
            ) : insightError || !insight ? (
              <div style={{ padding: '20px 0', fontSize: 17, color: '#374151' }}>Sorry — the insight couldn't be loaded. Please try again in a minute.</div>
            ) : !insight.eligible ? (
              <div style={{ padding: '20px 0', fontSize: 17, color: '#374151' }}>
                {insight.reason === 'no_hole_data'
                  ? `Insights need the hole-by-hole layout of ${insight.course_name || 'this course'}, which isn't set up yet.`
                  : `Insights start after ${insight.needed || 5} rounds at a course.`}
              </div>
            ) : (
              <>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#1f2937', lineHeight: 1.25, marginBottom: 6 }}>{insight.headline}</div>
                <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 14 }}>{formatDate(insight.date)} · compared with your earlier rounds there</div>
                {(insight.paragraphs || []).map((p, i) => (
                  <p key={i} style={{ fontSize: 17, lineHeight: 1.5, color: '#374151', margin: '0 0 12px' }}>{p}</p>
                ))}
                {insight.course_id && (
                  <button onClick={() => { setInsightOpen(false); setActiveTab('courses'); setSelectedCourse(insight.course_id); }}
                    style={{ background: 'none', border: 'none', padding: '6px 0', fontSize: 16, fontWeight: 700, color: '#166534', cursor: 'pointer' }}>
                    See this course in Stats →
                  </button>
                )}
              </>
            )}
            <button onClick={() => setInsightOpen(false)}
              style={{ display: 'block', width: '100%', marginTop: 16, padding: '14px 0', minHeight: 52, background: '#166534', color: '#fff', border: 'none', borderRadius: 12, fontSize: 18, fontWeight: 700, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      )}

      {activeTab === 'wallOfFame' && (
        <WallOfFameSection groupId={groupId} token={token} />
      )}
    </div>
  );
};

// ─── Wall of Fame Section ─────────────────────────────────────────────────────
// Two stacked sections: Notable Holes (eagles or better) and Notable Rounds
// (par or better). Sept 26, 2026 (Phase 2): real organizations read
// GET /api/player/scoring/wall-of-fame, which the server fills automatically on
// every score save (services/wallOfFameService.js). Only the Demo org still uses
// the hardcoded list below — its rounds are reseeded nightly.

const DEMO_GROUP_ID_WOF      = '0431809c-366b-48e3-ad8b-cecb9206a467';

const WALL_OF_FAME_DATA = {
  [DEMO_GROUP_ID_WOF]: {
    holes: [
      { player: 'Birdie Eagle',   achievement: 'Hole-in-One', emoji: '⭐',
        course: 'Pebble Beach Golf Links', hole: 7,  par: 3, score: 1,
        date: 'Apr 18, 2026' },
      { player: 'Fairway Woods',  achievement: 'Eagle',       emoji: '🦅',
        course: 'Pinehurst No. 2',        hole: 4,  par: 5, score: 3,
        date: 'Apr 2, 2026' },
      { player: 'Wedge Wizard',   achievement: 'Eagle',       emoji: '🦅',
        course: 'Pebble Beach Golf Links', hole: 18, par: 5, score: 3,
        date: 'Mar 22, 2026' },
      { player: 'Chip N. Putt',   achievement: 'Eagle',       emoji: '🦅',
        course: 'Pinehurst No. 2',        hole: 10, par: 4, score: 2,
        date: 'Feb 14, 2026' }
    ],
    rounds: [
      { player: 'Gimme Putt',    course: 'Pebble Beach Golf Links',
        gross: 67, par: 72, scoreToPar: -5, date: 'May 2, 2026' },
      { player: 'Sandy Bunker',  course: 'Pinehurst No. 2',
        gross: 70, par: 72, scoreToPar: -2, date: 'Apr 28, 2026' },
      { player: 'Hazard Hunter', course: 'Pebble Beach Golf Links',
        gross: 72, par: 72, scoreToPar: 0,  date: 'Mar 31, 2026' },
      { player: 'Par Avion',     course: 'Pinehurst No. 2',
        gross: 71, par: 72, scoreToPar: -1, date: 'Feb 18, 2026' }
    ]
  }
};

const formatScoreToPar = (n) => {
  if (n === 0) return 'E';
  if (n > 0) return `+${n}`;
  return `${n}`; // negative numbers already render with the minus sign
};

const HoleRow = ({ entry }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 5, background: '#f9fafb', border: '1px solid #d1d5db', color: '#4b5563' }}>
          {entry.achievement}
        </span>
        <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{entry.player}</span>
      </div>
      <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>{entry.date}</span>
    </div>
    <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
      {entry.course} · Hole {entry.hole} (par {entry.par}) — scored {entry.score}
    </div>
  </div>
);

const RoundRow = ({ entry }) => {
  const toParColor = entry.scoreToPar < 0 ? '#27500A' : '#9ca3af';
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ textAlign: 'center', minWidth: 48 }}>
        <div style={{ fontSize: 27, fontWeight: 700, color: '#111827', lineHeight: 1 }}>{entry.gross}</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: toParColor, marginTop: 3 }}>{formatScoreToPar(entry.scoreToPar)}</div>
      </div>
      <div style={{ flex: 1, borderLeft: '1px solid #e5e7eb', paddingLeft: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{entry.player}</span>
          <span style={{ fontSize: 12, color: '#9ca3af', whiteSpace: 'nowrap' }}>{entry.date}</span>
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 2 }}>{entry.course} · par {entry.par}</div>
      </div>
    </div>
  );
};

const SectionHeader = ({ label, descriptor }) => (
  <div style={{ margin: '4px 0 12px 0', paddingBottom: 7, borderBottom: '2px solid #111827' }}>
    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 3 }}>{descriptor}</div>
  </div>
);

const WOF_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// 'YYYY-MM-DD' -> 'Sep 26, 2026' (string math, no Date(): a DATE must not shift by time zone)
const formatWofDate = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${WOF_MONTHS[m - 1]} ${d}, ${y}`;
};

// API rows -> the entry shape HoleRow / RoundRow render.
const mapWallFromApi = (json) => ({
  holes: (json.holes || []).map(h => ({
    player: h.player_name, achievement: h.achievement, course: h.course_name || '',
    hole: h.hole_number, par: h.hole_par, score: h.hole_gross, date: formatWofDate(h.round_date)
  })),
  rounds: (json.rounds || []).map(r => ({
    player: r.player_name, course: r.course_name || '',
    gross: r.round_total_gross, par: r.round_total_par, scoreToPar: r.round_score_to_par,
    date: formatWofDate(r.round_date)
  }))
});

const WallOfFameSection = ({ groupId, token }) => {
  const isDemo = groupId === DEMO_GROUP_ID_WOF;
  const [apiData, setApiData] = useState(null);
  const [wofLoading, setWofLoading] = useState(!isDemo);
  const [wofError, setWofError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (isDemo || !token) { setWofLoading(false); return; }
    let cancelled = false;
    const load = async () => {
      setWofLoading(true);
      setWofError(false);
      try {
        const qs = `token=${encodeURIComponent(token)}${groupId ? `&group_id=${encodeURIComponent(groupId)}` : ''}`;
        const resp = await fetch(`${API_URL}/player/scoring/wall-of-fame?${qs}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = await resp.json();
        if (!cancelled) setApiData(mapWallFromApi(json));
      } catch (e) {
        console.error('Failed to load Wall of Fame:', e);
        if (!cancelled) setWofError(true);
      } finally {
        if (!cancelled) setWofLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [groupId, token, isDemo, reloadKey]);

  const loaded = isDemo ? WALL_OF_FAME_DATA[DEMO_GROUP_ID_WOF] : apiData;
  const groupData = loaded && (loaded.holes.length > 0 || loaded.rounds.length > 0) ? loaded : null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <span style={{ fontSize: 22 }}>🏌️</span>
        <span style={{ fontSize: 18, fontWeight: 700 }}>Wall of Fame</span>
      </div>
      <p style={{ fontSize: 13, color: '#374151', margin: '0 0 4px 0', lineHeight: 1.5 }}>
        Eagles, albatrosses, aces, and par-or-better rounds.
      </p>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 20px 0', lineHeight: 1.5, fontStyle: 'italic' }}>
        Achievements appear here automatically when scored through the app's Live Scoring or post-round entry.
      </p>

      {wofLoading && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 32, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
          Loading the Wall of Fame...
        </div>
      )}

      {!wofLoading && wofError && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
          Couldn't load the Wall of Fame.{' '}
          <button onClick={() => setReloadKey(k => k + 1)} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: 0 }}>
            Try again?
          </button>
        </div>
      )}

      {!wofLoading && !wofError && !groupData && (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🏆</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827', marginBottom: 6 }}>
            No legends on the wall yet
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6, maxWidth: 320, margin: '0 auto' }}>
            Score an eagle in a tracked round, or shoot par or better, and you'll be the first up here.
          </div>
        </div>
      )}

      {!wofLoading && !wofError && groupData && (
        <>
          <SectionHeader label="Notable Holes" descriptor="Eagles, albatrosses, and aces" />
          {groupData.holes.length > 0 ? (
            groupData.holes.map((entry, idx) => <HoleRow key={`hole-${idx}`} entry={entry} />)
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, textAlign: 'center', color: '#6b7280', fontSize: 13, marginBottom: 8 }}>
              No eagles yet. Score one and you'll be the first.
            </div>
          )}

          <div style={{ height: 16 }} />

          <SectionHeader label="Notable Rounds" descriptor="Par or better" />
          {groupData.rounds.length > 0 ? (
            groupData.rounds.map((entry, idx) => <RoundRow key={`round-${idx}`} entry={entry} />)
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>
              No par-or-better rounds yet. Shoot the lights out and earn your spot.
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ScoringStats;
